import { styled } from "../../../styles";

export const Container = styled("div", {
  display: "flex",
  flexDirection: "column",
  padding: "32px 40px 56px",
  gap: "32px",
});

export const Header = styled("header", {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
});

export const Title = styled("h1", {
  fontFamily: "$nunito",
  fontSize: "$heading-text-h3",
  color: "$black",
  margin: 0,
});

/**
 * Heading block, then the section's content. The two gaps differ on purpose
 * (EN-5146 UI review): the note belongs to its title, so it sits tight under
 * it, and the cards below need more air than one uniform gap gave them.
 */
export const Section = styled("section", {
  display: "flex",
  flexDirection: "column",
  gap: "20px",
});

/** Section title with the one-line note that explains it, kept together. */
export const SectionHeading = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "6px",
});

export const SectionTitle = styled("h2", {
  fontFamily: "$nunito",
  fontSize: "$heading-text-h5",
  color: "$black",
  margin: 0,
});

/**
 * A line of prose under a section title. Deliberately NOT width-capped:
 * the grids below it run the full width of the page, so a paragraph that
 * wrapped at a measure of its own read as text truncated mid-screen rather
 * than as a considered line length.
 */
export const SectionNote = styled("p", {
  fontFamily: "$openSans",
  fontSize: "$text-lg",
  color: "$gray-100",
  margin: 0,
  lineHeight: 1.6,
});

export const CounterGrid = styled("div", {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
  gap: "16px",
});

export const CounterCard = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  padding: "20px",
  borderRadius: "12px",
  border: "1px solid $gray-40",
  backgroundColor: "$white",
});

export const CounterLabel = styled("span", {
  fontFamily: "$openSans",
  fontSize: "$text-lg",
  fontWeight: 600,
  color: "$gray-100",
});

export const CounterValue = styled("span", {
  fontFamily: "$nunito",
  fontSize: "$heading-text-h4",
  color: "$black",
});

export const CounterDelta = styled("span", {
  fontFamily: "$openSans",
  fontSize: "$text-md",
  display: "inline-flex",
  gap: "6px",

  variants: {
    direction: {
      up: { color: "$success-dark" },
      down: { color: "$error-light" },
      flat: { color: "$gray-80" },
    },
  },

  defaultVariants: { direction: "flat" },
});

/**
 * The counting rule, printed on the card rather than hidden behind a tooltip.
 * These numbers look simple and are not: "active" is a union of three
 * activities and every count is per ward, not per patient. A reader who can't
 * see the rule will read the figure wrong.
 */
export const CounterRule = styled("p", {
  fontFamily: "$openSans",
  fontSize: "$text-md",
  color: "$gray-100",
  margin: "6px 0 0",
  paddingTop: "10px",
  borderTop: "1px solid $gray-30",
  lineHeight: 1.45,
});

export const NoticeCard = styled("div", {
  padding: "16px 20px",
  borderRadius: "12px",
  border: "1px solid $gray-40",
  borderLeft: "3px solid $primary-30",
  backgroundColor: "$gray-20",
  fontFamily: "$openSans",
  fontSize: "$text-lg",
  color: "$gray-100",
  lineHeight: 1.6,
});

export const NavGrid = styled("div", {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  gap: "16px",
});

export const NavGroupCard = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "10px",
  padding: "20px",
  borderRadius: "12px",
  border: "1px solid $gray-40",
  backgroundColor: "$white",
});

export const NavGroupTitle = styled("h3", {
  fontFamily: "$nunito",
  fontSize: "$text-xl",
  color: "$black",
  margin: 0,
});

export const NavList = styled("ul", {
  listStyle: "none",
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  margin: 0,
  padding: 0,
});

export const NavLink = styled("a", {
  fontFamily: "$openSans",
  fontSize: "$text-lg",
  color: "$primary",
  textDecoration: "none",
  cursor: "pointer",

  "&:hover": { textDecoration: "underline" },
});
